/**
 * Operations → Intake Verification.
 *
 * The records face of the Intake stretch: emails (reconstructed from
 * the leads they produced) as parent rows, the parsed leads nested
 * under each. The operator scans, expands, and marks an email verified
 * — the whole group flips at once (atomic, per the spec). Verified
 * groups drop off the queue. Empty queue = "All caught up."
 *
 * Read-only except the verify action. Lead corrections happen in
 * Airtable (the operations agent owns them); this surface only records
 * "an operator has eyeballed this and it's accurate."
 *
 * Spec: engineering/command-center/docs/port-notes/intake-verification.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Mail, Paperclip } from "lucide-react";
import type { IntakeEmailGroup, IntakeLead, LeadRouting } from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { cn } from "@/lib/utils";
import {
  useIntakeQueue,
  useVerifyGroup,
} from "@/lib/use-intake-verification";

export function IntakeVerificationPage() {
  const queue = useIntakeQueue();
  // Client-name filter (portal parity). A group is kept if its sender or any
  // of its leads matches; the count stays the total queue depth.
  const [query, setQuery] = useState("");

  if (queue.isLoading) {
    return (
      <div className="p-8 text-sm text-plane-muted">Loading…</div>
    );
  }

  if (queue.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="intake-error">
        Couldn't load the intake queue. It will retry automatically.
      </div>
    );
  }

  const groups = queue.data?.groups ?? [];
  const searching = query.trim() !== "";
  const visibleGroups = groups.filter(
    (g) =>
      matchesQuery(g.senderName, query) ||
      g.leads.some((l) => matchesQuery(l.name, query)),
  );

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="intake-verification">
      <BookHeading phaseKey="new-leads">
        <span
          className="text-sm text-plane-muted"
          data-testid="intake-unverified-count"
        >
          {groups.length === 0
            ? ""
            : `${groups.length} unverified`}
        </span>
      </BookHeading>

      <SearchBar value={query} onChange={setQuery} testId="intake-search" />

      {visibleGroups.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="intake-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "All caught up."}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {visibleGroups.map((group) => (
            <EmailGroupRow key={group.id} group={group} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EmailGroupRow({ group }: { group: IntakeEmailGroup }) {
  const [expanded, setExpanded] = useState(false);
  const verify = useVerifyGroup();

  const leadCount = group.leads.length;
  const routingSummary = summarizeRouting(group.leads);

  return (
    <li
      className="rounded-lg border bg-card text-card-foreground"
      data-testid="intake-email-group"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-3 text-left"
          aria-expanded={expanded}
          data-testid="intake-group-toggle"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">
              {group.senderName ?? group.senderEmail ?? "Unknown sender"}
            </span>
            {group.senderName && group.senderEmail ? (
              <span className="truncate text-xs text-muted-foreground">
                {group.senderEmail}
              </span>
            ) : null}
          </span>
        </button>

        <span className="shrink-0 text-xs text-muted-foreground">
          {formatRelative(group.receivedAt)}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {leadCount} {leadCount === 1 ? "lead" : "leads"}
        </span>
        <RoutingChip summary={routingSummary} />
        <Button
          size="sm"
          variant="outline"
          disabled={verify.isPending}
          onClick={() => verify.mutate(group.id)}
          data-testid="intake-verify-button"
        >
          {verify.isPending ? "Verifying…" : "Verify"}
        </Button>
      </div>

      {expanded ? (
        <ul className="flex flex-col gap-3 border-t px-4 py-3 pl-11">
          {group.leads.map((lead) => (
            <LeadDetail key={lead.id} lead={lead} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function LeadDetail({
  lead,
  asCard = false,
}: {
  lead: IntakeLead;
  /** When true, render with card chrome (border/padding) for standalone use on
   *  The Surface. Default (false) is the bare nested row inside an email group. */
  asCard?: boolean;
}) {
  const facets = [lead.caseType, lead.incidentState, lead.originatingFirmName]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join("  ·  ");

  return (
    <li
      className={cn(
        "flex flex-col gap-1",
        asCard && "rounded-lg border bg-card text-card-foreground px-4 py-3",
      )}
      data-testid="intake-lead"
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{lead.name}</span>
        <Badge variant={lead.signed ? "blue" : "slate"}>
          {lead.signed ? "Transfer" : "Lead"}
        </Badge>
        <SurfaceTrigger
          leadId={lead.id}
          className="ml-auto"
        />
      </div>

      <RoutingLine routing={lead.routing} />

      {facets ? (
        <div className="text-sm text-muted-foreground">{facets}</div>
      ) : null}

      <ContactLine lead={lead} />

      {lead.incidentDate ? (
        <div className="text-xs text-muted-foreground">
          Incident {lead.incidentDate}
        </div>
      ) : null}

      {lead.caseSummary ? <CaseSummary text={lead.caseSummary} /> : null}

      {lead.intakeDocs.length > 0 ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Paperclip className="h-3 w-3" />
          <span>
            {lead.intakeDocs.length}{" "}
            {lead.intakeDocs.length === 1 ? "attachment" : "attachments"}:
          </span>
          <span className="truncate">
            {lead.intakeDocs.map((d) => d.filename).join(" · ")}
          </span>
        </div>
      ) : null}

      {lead.internalNotes ? (
        <div
          className="rounded border-l-2 border-muted bg-muted/40 px-2 py-1 text-xs text-muted-foreground"
          data-testid="intake-lead-notes"
        >
          {lead.internalNotes}
        </div>
      ) : null}
    </li>
  );
}

function ContactLine({ lead }: { lead: IntakeLead }) {
  const parts: string[] = [];
  if (lead.phone) parts.push(lead.phone);
  if (lead.email) parts.push(lead.email);
  if (parts.length === 0) return null;
  return (
    <div className="text-xs text-muted-foreground">{parts.join("    ")}</div>
  );
}

function CaseSummary({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1 text-muted-foreground",
          "hover:text-foreground",
        )}
        data-testid="intake-summary-toggle"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Case summary
      </button>
      {open ? (
        <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{text}</p>
      ) : null}
    </div>
  );
}

/** Per-lead routing answer: did it move (and where), or is it stuck. */
function RoutingLine({ routing }: { routing: LeadRouting }) {
  if (routing.state === "moved") {
    const firms = routing.placements
      .map((p) => p.firmName ?? "Unknown firm")
      .join(", ");
    return (
      <div
        className="text-xs font-medium text-success"
        data-testid="routing-moved"
      >
        Routed → {firms || "a firm"}
      </div>
    );
  }
  if (routing.state === "stuck") {
    const label =
      routing.stuckReason === "unmatched"
        ? "Stuck — no matching firm (decline)"
        : "Stuck — missing info";
    return (
      <div
        className="text-xs font-medium text-error"
        data-testid="routing-stuck"
      >
        {label}
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground" data-testid="routing-pending">
      Awaiting routing
    </div>
  );
}

type RoutingSummary = "stuck" | "pending" | "moved";

/** Worst-first roll-up of a group's leads: any stuck → stuck, else any
 *  pending → pending, else everything moved. Lets the operator spot work
 *  without expanding the email. */
function summarizeRouting(leads: IntakeLead[]): RoutingSummary {
  if (leads.some((l) => l.routing.state === "stuck")) return "stuck";
  if (leads.some((l) => l.routing.state === "pending")) return "pending";
  return "moved";
}

/** Glance-level routing chip on the collapsed email row. */
function RoutingChip({ summary }: { summary: RoutingSummary }) {
  if (summary === "stuck") {
    return (
      <span
        className="shrink-0 rounded-full bg-error-bg px-2 py-0.5 text-xs font-medium text-error"
        data-testid="routing-chip-stuck"
      >
        Stuck
      </span>
    );
  }
  if (summary === "pending") {
    return (
      <span
        className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
        data-testid="routing-chip-pending"
      >
        Pending
      </span>
    );
  }
  return (
    <span
      className="shrink-0 rounded-full bg-success-bg px-2 py-0.5 text-xs font-medium text-success"
      data-testid="routing-chip-moved"
    >
      Routed
    </span>
  );
}

/** Compact relative time — "just now", "12m ago", "3h ago", "2d ago".
 *  Falls back to the instant's Pacific day for anything older than ~a week. */
function formatRelative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(then).toLocaleDateString(undefined, { timeZone: "America/Los_Angeles" });
}
